/**
 * IdleModeElement React component
 * 
 * @author    Maurizio Tidei
 * @copyright © 2020 contexagon GmbH
 */

import React from 'react';
import PropTypes from 'prop-types';
import {patchServerUrl} from "../../../config";
import {getMediaUrl} from '../../../operations/utils';
import MajusculeText from "./MajusculeText";
import {motion} from 'framer-motion';


/**
 * IdleModeElement
 * Supported types: Image, Fact, Statistic and Quote
 *
 * @author Maurizio Tidei
 */
class IdleModeElement extends React.Component {

    // animations keyframes definition
    textAnimationVariants = {
        visible: { opacity: 1, y: "0vw", transition: {ease: "easeOut", duration: this.props.fadeTime, delay: this.props.fadeTime} },
        hidden: { opacity: 0, y: "-7vw", transition: {ease: "easeIn", duration: this.props.fadeTime} },
    };

    imageAnimationVariants = {
        visible: { opacity: 1, y: "-1vw", scale: 1.0, transition: {ease: "easeOut", duration: this.props.fadeTime, delay: this.props.fadeTime} },
        hidden: { opacity: 0, y: "6vw", scale: 0.95, transition: {ease: "easeIn", duration: this.props.fadeTime} },
    };

    fullWidthImageAnimationVariants = {
        visible: {...this.imageAnimationVariants.visible, scale: 1.05},
        hidden: {...this.imageAnimationVariants.hidden, scale: 1.0},
    };

    borderAnimationVariantsHorizontal = {
        visible: { width: "100%", transition: {ease: "easeOut", duration: this.props.fadeTime}},
        hidden: { width: "0%", transition: {ease: "easeIn", duration: this.props.fadeTime, delay: this.props.fadeTime}}
    };

    borderAnimationVariantsVertical = {
        visible: { height: "100%", transition: {ease: "easeOut", duration: this.props.fadeTime}},
        hidden: { height: "0%", transition: {ease: "easeIn", duration: this.props.fadeTime, delay: this.props.fadeTime}}
    };

    render() {

        const idleElement = this.props.idleModeElement || {};
        const idleElementFields = idleElement.fields || {};

        const type = idleElementFields.type;

        const idleElementImage = this.getSingleReferencedObject(idleElementFields.image);
        const idleElementImageUrl = getMediaUrl(idleElementImage);
        //console.log("idle element info:", idleElement, idleElementImage, idleElementImageUrl);

        // animation handling
        let initial = "hidden";
        let animate = "visible";

        if(!this.props.visible) {
            initial = "visible";
            animate = "hidden";
        }

        // statistic element's image must be handled differently
        const imageDivStyle = {};
        const imageStyle = {width:"100%"};
        if(type === "statistic") {
            imageDivStyle.position = "absolute";
            imageDivStyle.left = 0;
            imageDivStyle.top = 0;
            imageDivStyle.height = "20vw";
            imageStyle.position = "absolute";
            imageStyle.top = "-50%";
        }

        const imageUrl = patchServerUrl(idleElementImageUrl);
        const imageElement = <motion.div className={"idle-element-image"} style={imageDivStyle} variants={type !== "image" && type !== "statistic" ? this.imageAnimationVariants : this.fullWidthImageAnimationVariants} initial={initial} animate={animate}>
            <img src={imageUrl} style={imageStyle}/></motion.div>;

        return <div>
            <div className={"idle-element " + this.props.className + " " + type}>
                {type !== "statistic" && imageElement}
                    <div className={"idle-element-textbox"}>
                        {type === "statistic" && imageElement}
                        <motion.div variants={this.textAnimationVariants} initial={initial} animate={animate}>
                            <MajusculeText text={idleElementFields.text} classNameMajuscules="idle-element-text-majuscule" classNameText="idle-element-text"/>
                        </motion.div>
                        {type !== "statistic" && type !== "image" && <>
                            <motion.div variants={this.borderAnimationVariantsHorizontal} initial={initial} animate={animate} className={"idle-element-border-top"} style={{backgroundColor: "#FFF", width: "100%"}}/>
                            <motion.div variants={this.borderAnimationVariantsHorizontal} initial={initial} animate={animate} className={"idle-element-border-bottom"} style={{backgroundColor:"#FFF", width:"100%"}}/>
                            <motion.div variants={this.borderAnimationVariantsVertical} initial={initial} animate={animate} className={"idle-element-border-left"} style={{backgroundColor: "#FFF", height: "100%"}}/>
                            <motion.div variants={this.borderAnimationVariantsVertical} initial={initial} animate={animate} className={"idle-element-border-right"} style={{backgroundColor:"#FFF", height:"100%"}}/>
                        </>}
                    </div>
                {type === "quote" && <motion.div variants={this.textAnimationVariants} initial={initial} animate={animate}>
                    <img src={patchServerUrl("/images/unikn/quote.png")} className={"idle-element-quote"}/></motion.div>}
            </div>
        </div>;
    }

    getSingleReferencedObject(field, index = 0) {
        if (!field || index < 0) return null;
        const items = this.props.findReferencedObjects(field);
        return items && items.length > index ? items[index] : null;
    }
}

IdleModeElement.propTypes = {
    idleModeElement: PropTypes.object.isRequired,
    boxName: PropTypes.string,
    findReferencedObjects: PropTypes.func.isRequired,
    className: PropTypes.string,
    visible: PropTypes.bool,
    fadeTime: PropTypes.number
};

export default IdleModeElement;
